import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Receipt, Maximize, Minimize2, CalendarDays, Home } from "lucide-react";
import { api, dt } from "@/lib/api";
import { useAuth, canWrite } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Cassa } from "@/components/Cassa";
import { Giornate } from "@/components/Giornate";
import { Scontrini } from "@/components/Scontrini";

const FullCassa = ({ write, onExit }) => {
  const nav = useNavigate();
  const [fs, setFs] = useState(!!document.fullscreenElement);
  useEffect(() => {
    const h = () => setFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);
  const toggleFs = () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.()).catch?.(() => {});
  const exit = (fn) => { if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); fn(); };
  return (
    <div data-testid="pos-fullpage" className="fixed inset-0 z-50 overflow-y-auto bg-slate-100 p-3 sm:p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Receipt className="h-5 w-5 text-sky-700" />Cassa</div>
        <div className="flex gap-2">
          <Button variant="outline" className="bg-white" data-testid="pos-fullscreen-button" onClick={toggleFs}>
            <Maximize className="mr-2 h-4 w-4" />{fs ? "Esci da schermo intero" : "Schermo intero"}
          </Button>
          <Button variant="outline" className="bg-white" data-testid="pos-exit-button" onClick={() => exit(onExit)}><Minimize2 className="mr-2 h-4 w-4" />Giornate e archivio</Button>
          <Button className="bg-sky-600 hover:bg-sky-700" data-testid="pos-back-menu-button" onClick={() => exit(() => nav("/app"))}><Home className="mr-2 h-4 w-4" />Torna al menu</Button>
        </div>
      </div>
      <Cassa write={write} />
    </div>
  );
};

export default function Corrispettivi() {
  const { user } = useAuth();
  const write = canWrite(user);
  const [st, setSt] = useState(null);
  const [full, setFull] = useState(true);
  const [tab, setTab] = useState("cassa");
  useEffect(() => { api.get("/corrispettivi/stato").then((r) => setSt(r.data)); }, []);
  if (!st) return null;
  if (!st.attivo) return (
    <div data-testid="receipts-inactive" className="mx-auto mt-16 max-w-lg rounded-xl border border-slate-200 bg-white p-8 text-center">
      <Receipt className="mx-auto mb-3 h-10 w-10 text-slate-400" />
      <h1 className="font-display text-xl font-bold">Corrispettivi telematici non attivi</h1>
      <p className="mt-2 text-sm text-slate-600">{st.abilitato ? `Il modulo è scaduto il ${dt(st.scadenza)}.` : "Il modulo è un pacchetto aggiuntivo."} Chiedi l'attivazione al tuo studio.</p>
    </div>
  );
  if (full) return <FullCassa write={write} onExit={() => { setFull(false); setTab("giornate"); }} />;
  return (
    <div>
      <PageHeader title="Corrispettivi telematici" subtitle="Cassa con lettore di codici a barre, chiusure giornaliere e registro dei corrispettivi">
        <Button className="bg-sky-600 hover:bg-sky-700" data-testid="pos-open-full-button" onClick={() => setFull(true)}><Maximize className="mr-2 h-4 w-4" />Apri cassa a tutta pagina</Button>
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex h-auto flex-wrap justify-start"><TabsTrigger value="cassa" data-testid="tab-pos">Cassa</TabsTrigger><TabsTrigger value="giornate" data-testid="tab-days"><CalendarDays className="mr-1 h-4 w-4" />Giornate e trasmissione</TabsTrigger><TabsTrigger value="scontrini" data-testid="tab-receipts"><Receipt className="mr-1 h-4 w-4" />Scontrini emessi</TabsTrigger></TabsList>
        <TabsContent value="cassa" className="mt-4"><Cassa write={write} /></TabsContent>
        <TabsContent value="giornate" className="mt-4"><Giornate write={write} /></TabsContent>
        <TabsContent value="scontrini" className="mt-4"><Scontrini /></TabsContent>
      </Tabs>
    </div>
  );
}
